import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getEvidenceById } from '../lib/evidence';
import { FANOUT_PAGES } from '../lib/site';

const page = FANOUT_PAGES.find((p) => p.path === '/erros-de-aplicacao');
const molloy = getEvidenceById('molloy-1991-standardized-mmse');
const attentionVariant = getEvidenceById('attention-variant-2011');
const community = getEvidenceById('anthony-1989-community-mmse');

const ANSWER =
  'Um erro de aplicação é qualquer mudança de instrução, ajuda, condição ou regra de pontuação capaz de alterar o desempenho sem refletir cognição. Quanto maior o desvio da aplicação padronizada, menor a comparabilidade do resultado com referências e aplicações anteriores.';

const REFERENCES = [molloy?.citation, attentionVariant?.citation, community?.citation].filter(Boolean);

const RELATED = [
  {
    to: '/como-aplicar',
    label: 'Como aplicar o Mini Mental',
    note: 'como preservar a padronização sem reproduzir o roteiro licenciado.'
  },
  {
    to: '/adaptacoes-sensoriais',
    label: 'Déficits sensoriais e motores',
    note: 'quando acomodar a barreira e quando a modificação altera a interpretação.'
  },
  {
    to: '/acompanhamento-e-queda-de-pontos',
    label: 'Comparar resultados ao longo do tempo',
    note: 'por que duas aplicações precisam ser realmente comparáveis.'
  },
  {
    to: '/interpretacao-dos-resultados',
    label: 'Como interpretar o resultado',
    note: 'o escore ganha significado apenas dentro do contexto clínico.'
  },
  {
    to: '/diagnostica-alzheimer',
    label: 'O Mini Mental diagnostica Alzheimer?',
    note: 'um resultado isolado não estabelece etiologia nem diagnóstico.'
  },
  {
    to: '/pontos-de-corte',
    label: 'Pontos de corte e referências',
    note: 'compare valores somente quando método e população forem compatíveis.'
  }
];

export default function ErrosDeAplicacao() {
  const reliability = molloy?.results;
  const communityResults = community?.results;

  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="improvisar">
        <h2 id="improvisar" className="text-2xl mb-5">1. Improvisar a aplicação</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Reformular, explicar além do previsto, induzir ou ajudar pode mudar a dificuldade da tarefa.
          A consequência não é necessariamente um erro em uma direção específica, mas perda de
          padronização e de comparabilidade com aplicações feitas de outro modo.
        </p>
        {reliability && (
          <p className="text-[var(--text-muted)]">
            Em {molloy.citation.split('.')[0]}, a versão padronizada reduziu a variância intrarrater em{' '}
            <strong>{reliability.intraraterVarianceReductionPct}%</strong> e a interrater em{' '}
            <strong>{reliability.interraterVarianceReductionPct}%</strong>; a correlação intraclasse
            passou de <strong>{reliability.iccBefore}</strong> para <strong>{reliability.iccAfter}</strong>.
            Isso mostra por que administração e pontuação consistentes importam para a reprodutibilidade.
          </p>
        )}
      </section>

      <section aria-labelledby="versoes">
        <h2 id="versoes" className="text-2xl mb-5">2. Misturar versões ou alternativas como se fossem equivalentes</h2>
        <p className="text-[var(--text-muted)]">
          Uma alternativa de tarefa não deve ser presumida numericamente equivalente a outra. No
          estudo de Hawkins e colaboradores com <strong>{attentionVariant?.sampleSize} adultos</strong>,
          uma variação comum na seção de atenção/cálculo produziu diferenças sistemáticas no total.
          Para seguimento, registre a versão e o método usados e procure mantê-los consistentes.
        </p>
      </section>

      <section aria-labelledby="barreiras">
        <h2 id="barreiras" className="text-2xl mb-5">3. Ignorar barreiras sensoriais ou motoras</h2>
        <p className="text-[var(--text-muted)]">
          Uma barreira visual, auditiva ou motora pode reduzir o desempenho por limitar o acesso à
          tarefa. Corrigir uma barreira habitual é diferente de modificar estímulos ou excluir itens.
          Quando a aplicação precisa ser modificada, o total não herda automaticamente a mesma
          interpretação da versão padrão. Veja{' '}
          <Link to="/adaptacoes-sensoriais" className=" text-[var(--primary)] hover:underline">
            como interpretar déficits sensoriais e motores
          </Link>.
        </p>
      </section>

      <section aria-labelledby="contexto">
        <h2 id="contexto" className="text-2xl mb-5">4. Não documentar um contexto clínico que limita a interpretação</h2>
        <p className="text-[var(--text-muted)]">
          Estado agudo, delirium, sedação, dor intensa, fadiga ou baixa atenção podem tornar um
          resultado menos representativo do funcionamento habitual. Não existe uma regra universal
          que permita transformar esses fatores em “pontos de desconto”. O correto é registrar o
          contexto e reconhecer a limitação na interpretação.
        </p>
        <p className="text-[var(--text-muted)] mt-4">
          Um exemplo frequente é a infecção aguda: em idosos, a gripe pode se apresentar como
          confusão mental e queda do desempenho cognitivo. Nesses casos, trate o quadro agudo
          antes de interpretar a pontuação (veja{' '}
          <a href="https://campanha.gripe/paciente/gripe-em-idosos/" className="text-[var(--primary)] hover:underline">
            gripe em idosos e sinais de alerta
          </a>
          ).
        </p>
      </section>

      <section aria-labelledby="comparar">
        <h2 id="comparar" className="text-2xl mb-5">5. Comparar aplicações que não são realmente comparáveis</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Mudança de versão, método, condições sensoriais, ajuda oferecida ou contexto clínico pode
          fazer duas pontuações parecerem comparáveis quando não são. Isso é especialmente importante
          quando a pergunta é se houve piora ao longo do tempo.
        </p>
        <p className="text-[var(--text-muted)]">
          Para interpretar trajetória, veja{' '}
          <Link to="/acompanhamento-e-queda-de-pontos" className=" text-[var(--primary)] hover:underline">
            queda de pontos no acompanhamento
          </Link>.
        </p>
      </section>

      <section aria-labelledby="diagnostico">
        <h2 id="diagnostico" className="text-2xl mb-5">6. Transformar o escore em diagnóstico</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Esse erro acontece depois da aplicação, mas é tão importante quanto os anteriores. Um total
          baixo pode aumentar a suspeita no contexto apropriado; não estabelece sozinho demência,
          delirium, Alzheimer ou outra etiologia.
        </p>
        {communityResults && (
          <p className="text-[var(--text-muted)]">
            No estudo comunitário britânico com <strong>{community.sampleSize.toLocaleString('pt-BR')}</strong>{' '}
            pessoas de 75 anos ou mais, apenas <strong>{communityResults.lowScoreDementiaOrDeliriumPct}%</strong>{' '}
            dos participantes com escore {communityResults.lowScoreThreshold} tinham demência ou
            delirium na avaliação aprofundada. O próprio estudo concluiu que o MMSE isolado não permite
            um diagnóstico psiquiátrico, nem mesmo provisório.
          </p>
        )}
        <p className="text-[var(--text-muted)] mt-4">
          Leia também{' '}
          <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">
            como interpretar o resultado
          </Link>{' '}e{' '}
          <Link to="/diagnostica-alzheimer" className=" text-[var(--primary)] hover:underline">
            por que o Mini Mental não diagnostica Alzheimer
          </Link>.
        </p>
      </section>

      <section aria-labelledby="checklist">
        <h2 id="checklist" className="text-2xl mb-5">Checklist de integridade da aplicação</h2>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 text-[var(--text-muted)]">
          <p className="font-bold text-[var(--text)]">
            versão definida → condições adequadas → instrução padronizada → sem ajuda não prevista →
            barreiras documentadas → regra de pontuação consistente → contexto registrado → interpretação clínica
          </p>
          <p className="mt-3">
            O objetivo é preservar comparabilidade. Este fluxo é educacional e não substitui o manual,
            a licença ou as instruções oficiais da versão utilizada.
          </p>
        </div>
      </section>
    </ArticleLayout>
  );
}
